import { useEffect, useRef, useState } from 'react'

/** Wait this long after the last keystroke before applying a search. */
export const SEARCH_DEBOUNCE_MS = 400

/**
 * While typing continues, apply the latest value at least this often
 * so a long burst does not wait forever on the debounce timer.
 */
export const SEARCH_THROTTLE_MS = 1000

/**
 * Decide when a search string should be committed.
 * Empty input commits immediately. Otherwise wait for a typing pause,
 * but no longer than the throttle window from the start of the burst.
 */
export function planSearchCommit({
  value,
  settled,
  now,
  burstStart,
  debounceMs,
  throttleMs,
}) {
  if (value === '') return { type: 'commit', value: '', burstStart: null }
  if (Object.is(value, settled)) return { type: 'idle', burstStart: null }
  const start = burstStart == null ? now : burstStart
  const elapsed = now - start
  const remainingThrottle = Math.max(0, throttleMs - elapsed)
  return {
    type: 'wait',
    delay: Math.min(debounceMs, remainingThrottle),
    burstStart: start,
  }
}

/**
 * Debounce a search value, with a throttle cap on continuous input.
 * An empty string is applied immediately so clearing the field does not wait.
 *
 * @param {string} value
 * @param {number} [debounceMs]
 * @param {number} [throttleMs]
 */
export function useDebouncedThrottledValue(
  value,
  debounceMs = SEARCH_DEBOUNCE_MS,
  throttleMs = SEARCH_THROTTLE_MS,
) {
  const [settled, setSettled] = useState(value)
  const latestRef = useRef(value)
  const burstStartRef = useRef(null)
  latestRef.current = value

  useEffect(() => {
    const plan = planSearchCommit({
      value,
      settled,
      now: Date.now(),
      burstStart: burstStartRef.current,
      debounceMs,
      throttleMs,
    })

    if (plan.type === 'commit') {
      burstStartRef.current = null
      setSettled('')
      return undefined
    }
    if (plan.type === 'idle') {
      burstStartRef.current = null
      return undefined
    }

    burstStartRef.current = plan.burstStart
    const timer = setTimeout(() => {
      burstStartRef.current = null
      setSettled(latestRef.current)
    }, plan.delay)
    return () => clearTimeout(timer)
  }, [value, settled, debounceMs, throttleMs])

  return settled
}
